import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import Button from '../shared/Button';

import { LIMIT_TIME_V1 } from './../../constants/subjectTypes';
import { profileUpdateStages, profileFetch } from "../../store/profile/actions";
import LanguageTestFailedOverview from "./LanguageTestFailedOverview";
import LanguageTestOverview from "./LanguageTestOverview";
import LanguageTestSuccess from "./LanguageTestSuccess";
import SubjectTestOverview from "./SubjectTestOverview";
import SubjectTestSuccess from "./SubjectTestSuccess";
import SubjectTestFailed from "./SubjectTestFailed";
// import TaskTestOverview from "./TaskTestOverview";
import baseStyles from 'custom/base.styl';
import { trackValues } from './../../tracker';
import { trackSiteId } from './../../config';
import { VERSION } from '../../constants/version';

const STAGES = {
  APP_OVERVIEW: 'application_overview',
  SUBJ_TEST: 'subject_test',
  SUBJ_TEST_START: 'subject_test_start',
  SUBJ_TEST_SUCCESS: 'subject_test_success',
  SUBJ_TEST_FAILED: 'subject_test_failed',
  LANG_TEST_FAIL: 'language_test_failed',
  LANG_TEST_SUCCESS: 'language_test_success',
}

const NAMES_TEST = {
  LANG_TEST: 'Language test',
  SUBJ_TEST: 'Subject test',
  TEST_TASK: 'Test task',
}

const TITLES = {
  LANG_TEST: 'Application Process Overview',
  LANG_TEST_FAILED: 'We are really sorry!',
  LANG_TEST_TIMEOUT: 'You’ve run out of time.',
  SUBJ_TEST: 'Great job! Two more stages left!',
  SUBJ_TEST_SUCCESS: 'Well done!',
  SUBJ_TEST_FAILED: 'We are really sorry!',
  LANG_TEST_SUCCESS: 'Great job! One more stage left!',
}

const SUBTITLE = {
  LANG_TEST: 'Your time is valued, so here’s an outline of our probation stages and the approximate completion time.',
}

const STEPS_TEST = {
  [NAMES_TEST.LANG_TEST]: {
    name: NAMES_TEST.LANG_TEST,
    questions: '24 questions',
    time: '~15 min',
  },
  [NAMES_TEST.SUBJ_TEST]: {
    name: NAMES_TEST.SUBJ_TEST,
    questions: '15 questions',
    time: '~15 min',
  },
  [NAMES_TEST.TEST_TASK]: {
    name: NAMES_TEST.TEST_TASK,
    questions: '1 any task',
    time: '~20-60 min',
  },
};

const getDataStages = (profile) => {
  let data = {}

  switch (profile.stage) {
    case STAGES.APP_OVERVIEW:
      data.testName = NAMES_TEST.LANG_TEST
      data.title = TITLES.LANG_TEST
      data.subtitle = SUBTITLE.LANG_TEST
      data.btn = `Start ${NAMES_TEST.LANG_TEST.toLowerCase()}`;
      STEPS_TEST[NAMES_TEST.LANG_TEST].active = true
      break
    case STAGES.LANG_TEST_FAIL:
      data.testName = null
      data.title = TITLES.LANG_TEST_FAILED
      STEPS_TEST[NAMES_TEST.LANG_TEST].failed = true
      break
    case STAGES.SUBJ_TEST:
      data.testName = NAMES_TEST.TEST_TASK
      data.title = TITLES.SUBJ_TEST
      data.btn = `Start ${NAMES_TEST.TEST_TASK.toLowerCase()}`;
      STEPS_TEST[NAMES_TEST.LANG_TEST].success = true
      STEPS_TEST[NAMES_TEST.SUBJ_TEST].active = true
      break
    case STAGES.SUBJ_TEST_FAILED:
      data.testName = null
      data.title = TITLES.SUBJ_TEST_FAILED
      STEPS_TEST[NAMES_TEST.LANG_TEST].success = true
      STEPS_TEST[NAMES_TEST.SUBJ_TEST].failed = true
      break
    case STAGES.LANG_TEST_SUCCESS:
      data.testName = NAMES_TEST.TEST_TASK
      data.title = TITLES.LANG_TEST_SUCCESS
      STEPS_TEST[NAMES_TEST.LANG_TEST].success = true
      STEPS_TEST[NAMES_TEST.TEST_TASK].active = true
      data.btn = `Begin the task`;
      break
    case STAGES.SUBJ_TEST_SUCCESS:
      data.testName = NAMES_TEST.TEST_TASK
      data.title = TITLES.SUBJ_TEST_SUCCESS
      data.btn = `Begin the task`;
      STEPS_TEST[NAMES_TEST.LANG_TEST].success = true
      STEPS_TEST[NAMES_TEST.SUBJ_TEST].success = true
      STEPS_TEST[NAMES_TEST.TEST_TASK].active = true
      break
    default:
      break
  }

  if (profile.scenario_version === VERSION.v9) {
    STEPS_TEST[NAMES_TEST.TEST_TASK].questions = 'Task in chosen subject'
    STEPS_TEST[NAMES_TEST.TEST_TASK].time = '~1-2 hours'
    delete STEPS_TEST[NAMES_TEST.SUBJ_TEST]
  }

  return data;
}

const PageApplicationOverview = (props) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (!profile) {
      dispatch(profileFetch())
    }
  }, [profile]);


  const profile = useSelector(state => state.profile.data.user);
  const isFetching = useSelector(state => state.profile.isFetching);

  if (!profile) {
    return null;
  }

  let data = {}
  if (profile) {
    data = getDataStages(profile);
  }

  const updateStage = () => {
    if (profile.stage === STAGES.APP_OVERVIEW || profile.stage === STAGES.SUBJ_TEST_SUCCESS || profile.stage === STAGES.LANG_TEST_SUCCESS) {
      const subject = profile.profile.subject_type;
      const stage = data.testName.toLowerCase().split(' ').join('_');
      const userId = profile.id;
      trackValues(`${stage}_start`, true, {
        context: { userId, site_id: trackSiteId, subject },
      });
    }

    dispatch(profileUpdateStages())
  }

  const renderStep = (step) => {
    return (
      <li key={step.name}
        className={classnames(baseStyles.step_panel2__item, {
          [baseStyles.step_panel2__item_active]: step.active,
          [baseStyles.step_panel2__item_success]: step.success,
          [baseStyles.step_panel2__item_failed]: step.failed,
        })}
      >
        <h6 className={baseStyles.step_panel2__title}>{step.name}</h6>
        <i className={classnames(baseStyles.step_panel2__icon, {[baseStyles.step_panel2__icon_v9]: (profile.scenario_version === VERSION.v9)})} />
        <span className={baseStyles.step_panel2__desc}>
          {step.questions}<br />
          {step.time}
        </span>
      </li>
    );
  }

  const renderStageOverview = () => {
    switch (profile.stage) {
      case STAGES.LANG_TEST_FAIL:
        const timeout = profile.profile.language_time_passed && profile.profile.language_time_passed >= LIMIT_TIME_V1
				const scoreLang = profile.profile.language_test_score || profile.language_test_score;
        return <LanguageTestFailedOverview timeout={timeout} score={scoreLang} />
      case STAGES.APP_OVERVIEW:
        return <LanguageTestOverview />
      case STAGES.SUBJ_TEST:
        return <SubjectTestOverview />
      case STAGES.LANG_TEST_SUCCESS:
        return <LanguageTestSuccess />
      case STAGES.SUBJ_TEST_SUCCESS:
        return <SubjectTestSuccess subject={profile.profile.subject_type} />
      case STAGES.SUBJ_TEST_FAILED:
				const scoreSubj = profile.profile.subject_test_score || profile.subject_test_score;
        return <SubjectTestFailed timeout={profile.profile.subject_test_status === 'timeout'} score={scoreSubj} />
      default:
        return
    }
  }

  return (
    <div>
      <div className={baseStyles.header}>
        <h1 className={classnames(baseStyles.title, baseStyles.mb_20)}>{data.title}</h1>
        <h2 className={classnames(baseStyles.title_xx_small, baseStyles.centered, baseStyles.pre_space)}>{data.subtitle}</h2>
      </div>
      <div className={classnames(baseStyles.content_board, baseStyles.centered, baseStyles.overview)}>
        <div className={baseStyles.step_panel2_wrap}>
          <ul className={baseStyles.step_panel2}>
            {Object.values(STEPS_TEST).map(s => renderStep(s))}
          </ul>
        </div>
        {renderStageOverview()}
        {data.testName &&
          <Button disabled={isFetching} onClick={updateStage}>{data.btn}</Button>
        }
      </div>
    </div>
  );
};

export default PageApplicationOverview;



// WEBPACK FOOTER //
// ./src/components/ApplicationOverview/PageApplicationOverview.js